import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, FolderKanban, Download } from 'lucide-react';
import { TimeFilter, PeriodType } from './TimeFilter';
import { StatCard } from './StatCard';
import { PageHeader } from '../common/ui';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';
import { useAuth } from '../../contexts/AuthContext';
import TimeNav from '../time/TimeNav';

interface ProjectStat {
  project_id: number;
  project_name: string;
  project_color: string;
  total_seconds: number;
  entry_count: number;
}

interface UserStat {
  user_id: number;
  user_name: string;
  total_seconds: number;
  entry_count: number;
}

interface UserProjectStat {
  user_id: number;
  user_name: string;
  project_id: number;
  project_name: string;
  project_color: string;
  total_seconds: number;
  entry_count: number;
}

interface DailyStat {
  date: string;
  total_seconds: number;
  entry_count: number;
}

interface DailyProjectStat {
  date: string;
  project_id: number;
  project_name: string;
  project_color: string;
  total_seconds: number;
}

interface TimeStatsData {
  period: { start: string; end: string };
  breakdown_granularity: 'daily' | 'weekly' | 'monthly';
  total_seconds: number;
  total_entries: number;
  average_per_day: number;
  by_project: ProjectStat[];
  by_user: UserStat[];
  user_project_matrix: UserProjectStat[];
  daily: DailyStat[];
  daily_by_project: DailyProjectStat[];
}

interface User {
  id: number;
  name: string;
  is_active?: boolean;
}

interface Project {
  id: number;
  name: string;
}

function formatHours(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatHoursDecimal(seconds: number): string {
  return (seconds / 3600).toFixed(1) + 'h';
}

const TimeStats: React.FC = () => {
  const { user } = useAuth() as any;
  const [searchParams, setSearchParams] = useSearchParams();

  const [period, setPeriod] = useState<PeriodType>(() => {
    const p = searchParams.get('period');
    if (p && ['7d', '30d', '365d', 'this_month', 'this_year', 'custom'].includes(p)) {
      return p as PeriodType;
    }
    const saved = getUserPreference('timeStats', 'period', '30d');
    if (['7d', '30d', '365d', 'this_month', 'this_year'].includes(saved)) {
      return saved as PeriodType;
    }
    return '30d';
  });

  const [customStart, setCustomStart] = useState(() => searchParams.get('start') || '');
  const [customEnd, setCustomEnd] = useState(() => searchParams.get('end') || '');
  const [filterUser, setFilterUser] = useState('');
  const [filterProject, setFilterProject] = useState('');

  const [stats, setStats] = useState<TimeStatsData | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [usersRes, projectsRes] = await Promise.all([
          axios.get('/api/users'),
          axios.get('/api/projects')
        ]);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.users || []);
        setProjects(projectsRes.data);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchFilters();
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('period', period);
      if (period === 'custom' && customStart && customEnd) {
        params.delete('period');
        params.set('start', customStart);
        params.set('end', customEnd);
      }
      if (filterUser) params.set('user_id', filterUser);
      if (filterProject) params.set('project_id', filterProject);

      const res = await axios.get(`/api/stats/time?${params.toString()}`);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching time stats:', err);
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd, filterUser, filterProject]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handlePeriodChange = (newPeriod: PeriodType, start?: string, end?: string) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setUserPreference('timeStats', 'period', newPeriod);
      setSearchParams({ period: newPeriod });
    } else if (start && end) {
      setCustomStart(start);
      setCustomEnd(end);
      setSearchParams({ period: 'custom', start, end });
    }
  };

  const handleExportCSV = () => {
    if (!stats) return;

    const rows: string[][] = [['User', 'Project', 'Hours', 'Entries']];

    if (stats.user_project_matrix.length > 0) {
      stats.user_project_matrix.forEach(row => {
        rows.push([
          row.user_name,
          row.project_name,
          (row.total_seconds / 3600).toFixed(2),
          row.entry_count.toString()
        ]);
      });
    } else {
      stats.by_user.forEach(u => {
        rows.push([u.user_name, 'All', (u.total_seconds / 3600).toFixed(2), u.entry_count.toString()]);
      });
    }

    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build user x project matrix for the table
  const buildMatrix = () => {
    if (!stats) return { userNames: [], projectNames: [], projectColors: [], matrix: {} as Record<string, Record<string, number>> };

    const userNames = Array.from(new Set(stats.user_project_matrix.map(r => r.user_name)));
    const projectSet = new Map<string, string>();
    stats.user_project_matrix.forEach(r => projectSet.set(r.project_name, r.project_color));
    const projectNames = Array.from(projectSet.keys());
    const projectColors = Object.fromEntries(projectSet);

    const matrix: Record<string, Record<string, number>> = {};
    userNames.forEach(u => {
      matrix[u] = {};
      projectNames.forEach(p => { matrix[u][p] = 0; });
    });
    stats.user_project_matrix.forEach(r => {
      matrix[r.user_name][r.project_name] = r.total_seconds;
    });

    return { userNames, projectNames, projectColors, matrix };
  };

  const { userNames, projectNames, projectColors, matrix } = buildMatrix();

  return (
    <div className="p-6">
      <TimeNav />

      <div className="flex justify-between items-center mb-6">
        <PageHeader title="Time Statistics" subtitle="" />
        <button
          onClick={handleExportCSV}
          disabled={!stats}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      <TimeFilter
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onChange={handlePeriodChange}
      />

      {/* Extra filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-4">
        {user?.time_master && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="">All employees</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name}{u.is_active === false ? ' (inactive)' : ''}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm"
          >
            <option value="">All projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        {(filterUser || filterProject) && (
          <button
            onClick={() => { setFilterUser(''); setFilterProject(''); }}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : !stats ? (
        <div className="text-center py-12 text-gray-500">No data available</div>
      ) : (
        <>
          {/* Overview cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatCard
              title="Total Time"
              value={formatHours(stats.total_seconds)}
              icon={Clock}
              subtitle={`${stats.total_entries} entries`}
            />
            <StatCard
              title="Average per Day"
              value={formatHours(stats.average_per_day)}
              icon={Clock}
              iconColor="bg-green-500"
            />
            <StatCard
              title="Active Projects"
              value={stats.by_project.length}
              icon={FolderKanban}
              iconColor="bg-purple-500"
            />
          </div>

          {/* Time by project */}
          {stats.by_project.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Time by Project</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {stats.by_project.map(p => {
                    const pct = stats.total_seconds > 0
                      ? Math.round((p.total_seconds / stats.total_seconds) * 100)
                      : 0;
                    return (
                      <div key={p.project_id}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.project_color }} />
                            <span className="text-sm font-medium text-gray-900">{p.project_name}</span>
                          </div>
                          <span className="text-sm text-gray-600">
                            {formatHours(p.total_seconds)} ({pct}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: p.project_color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Time by user (time_master only) */}
          {user?.time_master && stats.by_user.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Time by Employee</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Entries</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stats.by_user.map(u => (
                      <tr key={u.user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{u.user_name}</td>
                        <td className="px-6 py-3 text-sm text-gray-700 text-right tabular-nums">{formatHours(u.total_seconds)}</td>
                        <td className="px-6 py-3 text-sm text-gray-500 text-right">{u.entry_count}</td>
                        <td className="px-6 py-3 text-sm text-gray-500 text-right">
                          {stats.total_seconds > 0 ? Math.round((u.total_seconds / stats.total_seconds) * 100) : 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* User x Project matrix (time_master only) */}
          {user?.time_master && userNames.length > 0 && projectNames.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Employee x Project Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      {projectNames.map(pName => (
                        <th key={pName} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          <div className="flex items-center justify-end gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: projectColors[pName] }} />
                            {pName}
                          </div>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {userNames.map(uName => {
                      const userTotal = Object.values(matrix[uName]).reduce((s, v) => s + v, 0);
                      return (
                        <tr key={uName} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{uName}</td>
                          {projectNames.map(pName => (
                            <td key={pName} className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">
                              {matrix[uName][pName] > 0 ? formatHoursDecimal(matrix[uName][pName]) : '—'}
                            </td>
                          ))}
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right tabular-nums">
                            {formatHoursDecimal(userTotal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily breakdown */}
          {stats.daily.length > 0 && (() => {
            const maxSeconds = Math.max(...stats.daily.map(x => x.total_seconds));
            const granularity = stats.breakdown_granularity || 'daily';
            const dailyProjectMap: Record<string, DailyProjectStat[]> = {};
            (stats.daily_by_project || []).forEach(dp => {
              const dateKey = typeof dp.date === 'string' ? dp.date.split('T')[0] : dp.date;
              if (!dailyProjectMap[dateKey]) dailyProjectMap[dateKey] = [];
              dailyProjectMap[dateKey].push(dp);
            });

            const sectionTitle = granularity === 'monthly' ? 'Monthly Breakdown' : granularity === 'weekly' ? 'Weekly Breakdown' : 'Daily Breakdown';

            const formatLabel = (dateStr: string) => {
              const d = new Date(dateStr);
              if (granularity === 'monthly') {
                return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              }
              if (granularity === 'weekly') {
                const weekEnd = new Date(d);
                weekEnd.setDate(weekEnd.getDate() + 6);
                return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
              }
              return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
            };

            const labelWidth = granularity === 'weekly' ? 'w-36' : granularity === 'monthly' ? 'w-24' : 'w-24';

            return (
              <div className="bg-white rounded-lg shadow mb-8">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="text-base font-semibold text-gray-900">{sectionTitle}</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-2">
                    {stats.daily.map(d => {
                      const dateKey = typeof d.date === 'string' ? d.date.split('T')[0] : d.date;
                      const pct = maxSeconds > 0 ? (d.total_seconds / maxSeconds) * 100 : 0;
                      const dayProjects = dailyProjectMap[dateKey] || [];

                      return (
                        <div key={d.date} className="group relative flex items-center gap-3">
                          <span className={`text-xs text-gray-500 ${labelWidth} flex-shrink-0`}>
                            {formatLabel(d.date)}
                          </span>
                          <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                            <div className="flex h-5 rounded-full overflow-hidden" style={{ width: `${pct}%` }}>
                              {dayProjects.length > 0 ? dayProjects.map((dp, i) => {
                                const segPct = d.total_seconds > 0 ? (dp.total_seconds / d.total_seconds) * 100 : 0;
                                return (
                                  <div
                                    key={dp.project_id}
                                    className="h-full transition-all"
                                    style={{
                                      width: `${segPct}%`,
                                      backgroundColor: dp.project_color,
                                      borderRadius: i === 0 ? '9999px 0 0 9999px' : i === dayProjects.length - 1 ? '0 9999px 9999px 0' : '0'
                                    }}
                                    title={`${dp.project_name}: ${formatHours(dp.total_seconds)}`}
                                  />
                                );
                              }) : (
                                <div className="h-full w-full bg-naviter-blue rounded-full" />
                              )}
                            </div>
                          </div>
                          <span className="text-xs font-medium text-gray-700 w-16 text-right tabular-nums">
                            {formatHours(d.total_seconds)}
                          </span>
                          {dayProjects.length > 0 && (
                            <div className={`absolute ${granularity === 'weekly' ? 'left-40' : 'left-28'} top-full mt-1 z-10 hidden group-hover:block bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-48`}>
                              {dayProjects.map(dp => (
                                <div key={dp.project_id} className="flex items-center gap-2 py-0.5">
                                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dp.project_color }} />
                                  <span className="text-xs text-gray-700 flex-1">{dp.project_name}</span>
                                  <span className="text-xs font-medium text-gray-900 tabular-nums">{formatHours(dp.total_seconds)}</span>
                                  <span className="text-xs text-gray-400 tabular-nums w-8 text-right">
                                    {d.total_seconds > 0 ? Math.round((dp.total_seconds / d.total_seconds) * 100) : 0}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default TimeStats;
